// TenderQuotesAnalysis.jsx
// -----------------------------------------------------------------------------
// This component renders a bar chart showing aggregated tender quote data.
// It allows the user to:
//   - Compare lanes by different metrics:
//       * Best Unit Bid Value (lowest MinUnitPrice from lane summaries / quotes)
//       * Best Bid Value (lowest BidValue from quotes, with summary fallback)
//       * Most Quotes (number of quotes)
//       * More Loads (maximum Loads)
//       * Carriers (number of distinct carriers by grouped Source/Destination)
//       * Best Estimated Transit Days (lowest estimatedTransitDays)
//   - Aggregate either by Source or Destination per lane.
//
// Important:
// - Currency values are kept numeric for the chart calculations.
// - Currency formatting is delegated through valueFormatter so the bar chart
//   can render labels like "200 $" without breaking the numeric scale.
// - Expensive reductions are memoized and separated from the selected metric.
// - Carrier counts are calculated from quote CarrierId values with Sets.
//   Do not sum per-lane carrier counts, because that duplicates the same carrier
//   when several lanes share the same Source or Destination.
// -----------------------------------------------------------------------------

import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { MSelect } from "src/M_Components";
import TenderBarChart from "./TenderBarChart";
import { useTranslation } from "react-i18next";
import "./TenderQuotesAnalysis.css";

//region Constants

const COMPARISON = {
  BestUnitBidValue: "Best Unit Bid Value",
  BestBidValue: "Best Bid Value",
  MostQuotes: "Most Quotes",
  MoreLoads: "More Loads",
  Carriers: "Carriers",
  BestEstimatedTransitDays: "Best Estimated Transit Days",
};

const SOURCE_OR_DEST = {
  Source: "Source",
  Destination: "Destination",
};

//endregion

//region Helpers

const firstFiniteNumber = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;

    if (typeof candidate === "string" && candidate.trim() === "") {
      continue;
    }

    const value = Number(candidate);

    if (Number.isFinite(value)) {
      return value;
    }
  }

  return null;
};

const firstPositiveNumber = (...candidates) => {
  for (const candidate of candidates) {
    const value = firstFiniteNumber(candidate);

    if (value != null && value > 0) {
      return value;
    }
  }

  return null;
};

const normalizeCurrency = (currency) => String(currency ?? "").trim();

const formatNumber = (value, locale) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));

const isCurrencyComparison = (comparison) =>
  comparison === COMPARISON.BestUnitBidValue ||
  comparison === COMPARISON.BestBidValue;

const getLaneId = (lane) => {
  const value =
    lane?.LaneId ??
    lane?.laneId ??
    lane?.LaneID ??
    lane?.laneID ??
    lane?.TenderLaneId ??
    lane?.tenderLaneId ??
    lane?.TenderLaneID ??
    lane?.tenderLaneID ??
    lane?.Id ??
    lane?.id ??
    lane?.Lane?.Id ??
    lane?.Lane?.id ??
    lane?.lane?.Id ??
    lane?.lane?.id ??
    null;

  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const getQuoteLaneId = (quote) => {
  const value =
    quote?.LaneId ??
    quote?.laneId ??
    quote?.LaneID ??
    quote?.laneID ??
    quote?.TenderLaneId ??
    quote?.tenderLaneId ??
    quote?.TenderLaneID ??
    quote?.tenderLaneID ??
    quote?.Lane?.Id ??
    quote?.Lane?.id ??
    quote?.lane?.Id ??
    quote?.lane?.id ??
    quote?.TenderLane?.LaneId ??
    quote?.TenderLane?.laneId ??
    quote?.TenderLane?.Id ??
    quote?.TenderLane?.id ??
    quote?.tenderLane?.LaneId ??
    quote?.tenderLane?.laneId ??
    quote?.tenderLane?.Id ??
    quote?.tenderLane?.id ??
    null;

  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const getSummaryLaneId = (summary) => {
  const value =
    summary?.LaneId ??
    summary?.laneId ??
    summary?.LaneID ??
    summary?.laneID ??
    summary?.TenderLaneId ??
    summary?.tenderLaneId ??
    summary?.TenderLaneID ??
    summary?.tenderLaneID ??
    summary?.Id ??
    summary?.id ??
    null;

  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const getLaneSource = (lane) =>
  String(
    lane?.LaneSource ??
    lane?.laneSource ??
    lane?.SourceLocation ??
    lane?.sourceLocation ??
    lane?.Origin ??
    lane?.origin ??
    lane?.Lane?.LaneSource ??
    lane?.Lane?.laneSource ??
    lane?.lane?.LaneSource ??
    lane?.lane?.laneSource ??
    lane?.Lane?.SourceLocation ??
    lane?.Lane?.sourceLocation ??
    lane?.lane?.SourceLocation ??
    lane?.lane?.sourceLocation ??
    ""
  ).trim();

const getLaneDestination = (lane) =>
  String(
    lane?.LaneDestination ??
    lane?.laneDestination ??
    lane?.DestinationLocation ??
    lane?.destinationLocation ??
    lane?.Destination ??
    lane?.destination ??
    lane?.Lane?.LaneDestination ??
    lane?.Lane?.laneDestination ??
    lane?.lane?.LaneDestination ??
    lane?.lane?.laneDestination ??
    lane?.Lane?.DestinationLocation ??
    lane?.Lane?.destinationLocation ??
    lane?.lane?.DestinationLocation ??
    lane?.lane?.destinationLocation ??
    ""
  ).trim();

const getQuoteUnitPrice = (quote) =>
  firstPositiveNumber(
    quote?.UnitPrice,
    quote?.unitPrice,
    quote?.BidUnitValue,
    quote?.bidUnitValue,
    quote?.UnitBidValue,
    quote?.unitBidValue,
    quote?.PricePerLoad,
    quote?.pricePerLoad
  );

const getQuoteBidValue = (quote) =>
  firstPositiveNumber(
    quote?.BidValue,
    quote?.bidValue,
    quote?.Bid,
    quote?.bid,
    quote?.TotalBidValue,
    quote?.totalBidValue
  );

const getQuoteLoads = (quote) =>
  firstFiniteNumber(
    quote?.Loads,
    quote?.loads,
    quote?.TotalLoads,
    quote?.totalLoads
  );

const getQuoteEstimatedTransitDays = (quote) =>
  firstFiniteNumber(
    quote?.EstimatedTransitDays,
    quote?.estimatedTransitDays,
    quote?.TransitDays,
    quote?.transitDays,
    quote?.ETD,
    quote?.etd
  );

const getQuoteCarrierId = (quote) => {
  const value =
    quote?.CarrierId ??
    quote?.carrierId ??
    quote?.CarrierID ??
    quote?.carrierID ??
    quote?.Carrier?.Id ??
    quote?.Carrier?.id ??
    quote?.carrier?.Id ??
    quote?.carrier?.id ??
    quote?.Carrier?.CarrierId ??
    quote?.Carrier?.carrierId ??
    quote?.carrier?.CarrierId ??
    quote?.carrier?.carrierId ??
    null;

  return value != null ? String(value) : null;
};

const getSummaryUnitValue = (summary) =>
  firstPositiveNumber(
    summary?.MinUnitPrice,
    summary?.minUnitPrice,
    summary?.BestUnitValue,
    summary?.bestUnitValue,
    summary?.BestUnitPrice,
    summary?.bestUnitPrice,
    summary?.BidUnitValue,
    summary?.bidUnitValue,
    summary?.UnitBidValue,
    summary?.unitBidValue
  );

const getSummaryBidValue = (summary) =>
  firstPositiveNumber(
    summary?.MinBidValue,
    summary?.minBidValue,
    summary?.BestBidValue,
    summary?.bestBidValue,
    summary?.BidValue,
    summary?.bidValue,
    summary?.MinBid,
    summary?.minBid,
    summary?.TotalBidValue,
    summary?.totalBidValue
  );

const getSummaryTotalQuotes = (summary) =>
  firstFiniteNumber(summary?.TotalQuotes, summary?.totalQuotes);

const getSummaryMaxLoads = (summary) =>
  firstFiniteNumber(summary?.MaxLoads, summary?.maxLoads);

const getSummaryMinEstimatedTransitDays = (summary) =>
  firstFiniteNumber(
    summary?.MinEstimatedTransitDays,
    summary?.minEstimatedTransitDays
  );

//endregion

//region Component

function TenderQuotesAnalysis({
  currency = null,
  lanes = [],
  laneSummaries = [],
  quotes = [],
}) {
  const { t, i18n } = useTranslation();

  //region Local UI state

  const [comparison, setComparison] = useState(COMPARISON.BestUnitBidValue);
  const [sourceOrDest, setSourceOrDest] = useState(SOURCE_OR_DEST.Destination);

  //endregion

  //region Formatting

  const currencyLabel = useMemo(() => normalizeCurrency(currency), [currency]);

  const valueFormatter = useCallback(
    (value) => {
      const numericValue = Number(value);

      if (!Number.isFinite(numericValue)) {
        return value ?? "";
      }

      const formattedNumber = formatNumber(
        numericValue,
        i18n?.resolvedLanguage || i18n?.language
      );

      if (!isCurrencyComparison(comparison) || !currencyLabel) {
        return formattedNumber;
      }

      return `${formattedNumber} ${currencyLabel}`;
    },
    [comparison, currencyLabel, i18n?.resolvedLanguage, i18n?.language]
  );

  //endregion

  //region Indexed source data

  const noData =
    !Array.isArray(lanes) ||
    lanes.length === 0 ||
    (!Array.isArray(laneSummaries) && !Array.isArray(quotes)) ||
    ((laneSummaries || []).length === 0 && (quotes || []).length === 0);

  const summaryByLaneId = useMemo(() => {
    const map = new Map();

    for (const row of Array.isArray(laneSummaries) ? laneSummaries : []) {
      const laneId = getSummaryLaneId(row);
      if (laneId == null) continue;

      map.set(laneId, row);
    }

    return map;
  }, [laneSummaries]);

  const quoteMetricsByLaneId = useMemo(() => {
    const map = new Map();

    for (const quote of Array.isArray(quotes) ? quotes : []) {
      const laneId = getQuoteLaneId(quote);
      if (laneId == null) continue;

      const current = map.get(laneId) || {
        totalQuotes: 0,
        minUnitPrice: null,
        minBidValue: null,
        maxLoads: null,
        minEstimatedTransitDays: null,
        carrierIds: new Set(),
      };

      current.totalQuotes += 1;

      const unitPrice = getQuoteUnitPrice(quote);
      const bidValue = getQuoteBidValue(quote);
      const loads = getQuoteLoads(quote);
      const estimatedTransitDays = getQuoteEstimatedTransitDays(quote);
      const carrierId = getQuoteCarrierId(quote);

      if (carrierId != null) {
        current.carrierIds.add(carrierId);
      }

      if (unitPrice != null) {
        current.minUnitPrice =
          current.minUnitPrice == null
            ? unitPrice
            : Math.min(current.minUnitPrice, unitPrice);
      }

      if (bidValue != null) {
        current.minBidValue =
          current.minBidValue == null
            ? bidValue
            : Math.min(current.minBidValue, bidValue);
      }

      if (loads != null) {
        current.maxLoads =
          current.maxLoads == null ? loads : Math.max(current.maxLoads, loads);
      }

      if (estimatedTransitDays != null) {
        current.minEstimatedTransitDays =
          current.minEstimatedTransitDays == null
            ? estimatedTransitDays
            : Math.min(current.minEstimatedTransitDays, estimatedTransitDays);
      }

      map.set(laneId, current);
    }

    return map;
  }, [quotes]);

  //endregion

  //region Grouped metrics

  /**
   * Heavy aggregation is done here once per data/source change.
   *
   * Changing only the selected comparison does not rescan lanes or quotes.
   *
   * Important:
   * - Carriers are counted as DISTINCT CarrierId values per grouped label.
   * - We intentionally do not sum summary DistinctCarriers values here, because
   *   those values are lane-level aggregates and would duplicate the same carrier
   *   when several lanes share the same Source or Destination.
   */
  const groupedMetrics = useMemo(() => {
    if (noData) return new Map();

    const result = new Map();

    for (const lane of Array.isArray(lanes) ? lanes : []) {
      const laneId = getLaneId(lane);
      if (laneId == null) continue;

      const label =
        sourceOrDest === SOURCE_OR_DEST.Source
          ? getLaneSource(lane)
          : getLaneDestination(lane);

      if (!label) continue;

      const summary = summaryByLaneId.get(laneId) || null;
      const quoteMetrics = quoteMetricsByLaneId.get(laneId) || null;

      if (!summary && !quoteMetrics) continue;

      if (!result.has(label)) {
        result.set(label, {
          totalQuotes: 0,
          minUnitPrice: Number.POSITIVE_INFINITY,
          minBidValue: Number.POSITIVE_INFINITY,
          maxLoads: 0,
          minEtd: Number.POSITIVE_INFINITY,
          carrierIds: new Set(),
        });
      }

      const aggregate = result.get(label);

      const totalQuotes =
        getSummaryTotalQuotes(summary) ?? quoteMetrics?.totalQuotes ?? 0;

      const minUnitPrice = firstPositiveNumber(
        getSummaryUnitValue(summary),
        quoteMetrics?.minUnitPrice
      );

      const minBidValue = firstPositiveNumber(
        quoteMetrics?.minBidValue,
        getSummaryBidValue(summary)
      );

      const maxLoads =
        getSummaryMaxLoads(summary) ?? quoteMetrics?.maxLoads ?? 0;

      const minEtd =
        getSummaryMinEstimatedTransitDays(summary) ??
        quoteMetrics?.minEstimatedTransitDays ??
        null;

      aggregate.totalQuotes += totalQuotes;

      if (minUnitPrice != null) {
        aggregate.minUnitPrice = Math.min(
          aggregate.minUnitPrice,
          minUnitPrice
        );
      }

      if (minBidValue != null) {
        aggregate.minBidValue = Math.min(aggregate.minBidValue, minBidValue);
      }

      if (maxLoads != null) {
        aggregate.maxLoads = Math.max(aggregate.maxLoads, maxLoads);
      }

      if (minEtd != null) {
        aggregate.minEtd = Math.min(aggregate.minEtd, minEtd);
      }

      if (quoteMetrics?.carrierIds instanceof Set) {
        for (const carrierId of quoteMetrics.carrierIds) {
          aggregate.carrierIds.add(carrierId);
        }
      }
    }

    return result;
  }, [lanes, sourceOrDest, summaryByLaneId, quoteMetricsByLaneId, noData]);

  //endregion

  //region Select options

  const comparisonOptions = useMemo(() => {
    const hasCarrierCounts = Array.from(groupedMetrics.values()).some(
      (metrics) =>
        metrics?.carrierIds instanceof Set && metrics.carrierIds.size > 0
    );

    const base = [
      {
        label: t("Best Unit Bid Value"),
        value: COMPARISON.BestUnitBidValue,
      },
      {
        label: t("Best Bid Value"),
        value: COMPARISON.BestBidValue,
      },
      {
        label: t("Most Quotes"),
        value: COMPARISON.MostQuotes,
      },
      {
        label: t("More Loads"),
        value: COMPARISON.MoreLoads,
      },
      {
        label: t("Best Estimated Transit Days"),
        value: COMPARISON.BestEstimatedTransitDays,
      },
    ];

    if (hasCarrierCounts) {
      base.splice(4, 0, {
        label: t("Carriers"),
        value: COMPARISON.Carriers,
      });
    }

    return base;
  }, [t, groupedMetrics]);

  const sourceOptions = useMemo(
    () => [
      { label: t("Source"), value: SOURCE_OR_DEST.Source },
      { label: t("Destination"), value: SOURCE_OR_DEST.Destination },
    ],
    [t]
  );

  //endregion

  //region Chart data

  /**
   * Lightweight projection.
   *
   * This is the only calculation that runs when the selected comparison changes.
   */
  const chartData = useMemo(() => {
    if (!groupedMetrics.size) return {};

    const finalChart = {};

    for (const [label, data] of groupedMetrics.entries()) {
      let value = 0;

      switch (comparison) {
        case COMPARISON.BestUnitBidValue:
          value =
            data.minUnitPrice !== Number.POSITIVE_INFINITY
              ? data.minUnitPrice
              : 0;
          break;

        case COMPARISON.BestBidValue:
          value =
            data.minBidValue !== Number.POSITIVE_INFINITY
              ? data.minBidValue
              : 0;
          break;

        case COMPARISON.MostQuotes:
          value = data.totalQuotes;
          break;

        case COMPARISON.MoreLoads:
          value = data.maxLoads;
          break;

        case COMPARISON.Carriers:
          value =
            data.carrierIds instanceof Set ? data.carrierIds.size : 0;
          break;

        case COMPARISON.BestEstimatedTransitDays:
          value = data.minEtd !== Number.POSITIVE_INFINITY ? data.minEtd : 0;
          break;

        default:
          value = 0;
      }

      finalChart[label] = Number.isFinite(Number(value)) ? Number(value) : 0;
    }

    return finalChart;
  }, [groupedMetrics, comparison]);

  //endregion

  //region Render

  return (
    <div className="tqa-root">
      <span className="tqa-title">{t("Analysis Responses")}</span>

      <div className="tqa-controls">
        <div className="tqa-control">
          <MSelect
            label={t("Comparison")}
            options={comparisonOptions}
            value={
              comparisonOptions.find((opt) => opt.value === comparison) || null
            }
            onChangeValue={(selected) => {
              if (!selected) {
                setComparison(COMPARISON.BestUnitBidValue);
                return;
              }

              setComparison(selected.value);
            }}
            autocomplete
            required
          />
        </div>

        <div className="tqa-control">
          <MSelect
            label={t("Source")}
            options={sourceOptions}
            value={
              sourceOptions.find((opt) => opt.value === sourceOrDest) || null
            }
            onChangeValue={(selected) => {
              if (!selected) {
                setSourceOrDest(SOURCE_OR_DEST.Destination);
                return;
              }

              setSourceOrDest(selected.value);
            }}
            autocomplete
            required
          />
        </div>
      </div>

      {!noData ? (
        <div className="tqa-chart-wrapper">
          <div className="tqa-chart-inner">
            <TenderBarChart data={chartData} valueFormatter={valueFormatter} />
          </div>
        </div>
      ) : (
        <div className="tqa-no-data">
          <span className="tqa-no-data-text">{t("No data available")}</span>
        </div>
      )}
    </div>
  );

  //endregion
}

//endregion

TenderQuotesAnalysis.propTypes = {
  tenderId: PropTypes.number,
  routeId: PropTypes.number,
  isSpotTender: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  lanes: PropTypes.array.isRequired,
  laneSummaries: PropTypes.array.isRequired,
  quotes: PropTypes.array,
  workflowMode: PropTypes.string,
  analysisRun: PropTypes.object,
  analysisSolutionReady: PropTypes.bool,
};

export default React.memo(TenderQuotesAnalysis);